import type { ColourDepth } from "@qendo/theme";
import { type DateArg, differenceInDays, isPast } from "date-fns";
import { useEffect, useMemo } from "react";
import { StyleSheet, View, type ViewProps } from "react-native";
import { AnimatedCircularProgress } from "react-native-circular-progress";
import Animated, { useSharedValue, withTiming } from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Subtitle, Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";

import { Space } from "~/constants/sizing";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { formatDateString, formatShortDateForHumans } from "~/util/dates";
import { extractPeriods } from "~/util/qendo/cycle";
import { plural } from "~/util/string";

export type CycleTrackerProps = {
    forDate?: Date | DateArg<Date> | null;
    hidden?: boolean;
} & Omit<ViewProps, "children">;

const CIRCLE_SIZE = 200;

type BackgroundColourKey = Extract<ColourDepth, 0 | 200 | 300 | 400 | 500 | 600 | 700>;
type TintColourKey = Extract<ColourDepth, 300 | 400 | 500 | 600 | 700 | 800 | 900>;

export const CycleTracker = ({ forDate, hidden, ...viewProps }: CycleTrackerProps) => {
    const { colours, cycle_colours, background } = useTheme();
    const { cycle } = useProfile();

    const today = formatDateString(new Date());
    const date = forDate ? formatDateString(forDate) : today;

    const { prev, current, next } = useMemo(() => extractPeriods(cycle, date), [cycle, date]);

    const is_period = Boolean(current);
    const estimated_cycle = cycle.tracked ? ((cycle.estimate ? cycle.estimated_cycle : null) ?? cycle.cycle) : 0;
    const estimated_period = cycle.tracked ? ((cycle.estimate ? cycle.estimated_period : null) ?? cycle.period) : 0;
    const cycle_started = current?.start_at ?? prev?.start_at ?? null;
    const period_ended = current?.ended_at ?? prev?.ended_at ?? null;
    const cycle_ends = next?.started_at ?? null;
    const cycle_day = cycle_started ? differenceInDays(date, cycle_started) : -1;
    const period_day = is_period ? cycle_day : -1;

    const maxHeight = useSharedValue(cycle.tracked && !hidden ? CIRCLE_SIZE : 0);

    // biome-ignore lint/correctness/useExhaustiveDependencies(maxHeight): shared value
    useEffect(() => {
        maxHeight.value = withTiming(cycle.tracked && !hidden ? CIRCLE_SIZE : 0, { duration: 100 });
    }, [cycle.tracked, hidden]);

    const days_until_next_cycle = cycle_ends
        ? differenceInDays(cycle_ends, date)
        : estimated_cycle > 0 && cycle_day >= 0
          ? estimated_cycle - cycle_day
          : -1;

    const cycle_overdue_by = cycle_day > 0 && days_until_next_cycle <= 0 ? Math.abs(days_until_next_cycle) + 1 : -1;

    const total_days = is_period ? estimated_period : estimated_cycle;
    const percent_filled = total_days > 0 ? (is_period ? period_day : cycle_day) / total_days : 0;
    const overdue = percent_filled >= 1;
    const fill = Math.max((percent_filled % 1) * 100, 0.1);

    const track_background = (
        overdue ? Math.max(200, Math.min(700, Math.floor(percent_filled) * 100)) : 100
    ) as BackgroundColourKey;

    const tint = (track_background + 200) as TintColourKey;
    const backgroundColor = overdue ? cycle_colours[track_background] : colours.background.secondary;
    const tintColor = cycle_colours[tint];

    const [subtitle, title] = (!is_period &&
        days_until_next_cycle > 0 && [
            "Period in",
            `${days_until_next_cycle} ${plural("day", days_until_next_cycle)}`,
        ]) ||
        (cycle_started === date && isPast(date) && ["Period started", formatShortDateForHumans(date)]) ||
        (is_period && period_ended === date && isPast(date) && ["Period ended", formatShortDateForHumans(date)]) ||
        (is_period && ["Period", `Day ${period_day + 1}`]) ||
        (cycle_overdue_by > 0 && ["Period late", `${cycle_overdue_by} ${plural("day", cycle_overdue_by)}`]) || [
            "",
            formatShortDateForHumans(date),
        ];

    const circle_props = {
        fill,
        tintColor,
        backgroundColor,
        children: () => (
            <>
                <View style={styles.tracking}>
                    <Subtitle>{subtitle}</Subtitle>
                    <Title>{title}</Title>
                </View>
                <Can ability="update" section="journal">
                    <Button
                        title="Log period"
                        variant="secondary"
                        size="md"
                        href={{
                            pathname: "/periods/create",
                            params: { date: date },
                        }}
                    />
                </Can>
            </>
        ),
    };

    return (
        <Can
            ability="view"
            section="cycle"
            show={() => (
                <Animated.View {...viewProps} style={[styles.container, viewProps.style, { maxHeight }]}>
                    <AnimatedCircularProgress
                        {...circle_props}
                        size={CIRCLE_SIZE}
                        width={12}
                        lineCap="round"
                        rotation={180}
                        childrenContainerStyle={{ ...styles.children, ...background.primary }}
                    />
                </Animated.View>
            )}
        />
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
        overflow: "hidden",
    },
    children: {
        alignItems: "center",
        gap: Space.md,
    },
    tracking: {
        alignItems: "center",
        gap: Space.xxs,
    },
});
